import React from 'react'
import { Box, Chip, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import CancelOutlined from '@mui/icons-material/CancelOutlined'
import Card from './Card'
import Txt from './Txt'
import Button from './Button'
import { MenuImportItem } from '../mutations/importMenu'
import { MenuImportReport } from '../queries/menuImportReport'

// Jede Sektion ist eine eigene Karte, der Rahmen darum nur ein Stapel mit Abstand.
const sxPage = { display: 'flex', flexDirection: 'column', gap: '1.25rem' } as const

const sxNumberCell = { textAlign: 'right' } as const
const sxNumberHeadCell = { fontWeight: 'bold', textAlign: 'right' } as const
const sxFooterButtons = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	gap: '1rem',
} as const
const sxHeadCell = { fontWeight: 'bold' } as const
const sxSectionTitle = { mb: '0.5rem' } as const
const sxSource = { mt: '0.5rem' } as const
const sxCategory = { mt: '1rem' } as const
const sxItemNameCell = { width: '30%' } as const
const sxStatusCell = { width: '10rem' } as const
const sxStatus = { display: 'flex', alignItems: 'center', gap: '0.4rem' } as const
const sxGaps = { display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.75rem' } as const
const sxGap = { display: 'flex', alignItems: 'center', gap: '0.3rem' } as const

// Warum ein Artikel draußen bleibt, kurz neben dem roten Kreuz.
const DROP_REASONS: Record<MenuImportItem['status'], string> = {
	IMPORTABLE: '',
	PREPACKED: 'vorverpackt',
	ALCOHOL: 'Alkohol',
	UNDECLARED: 'Kennzeichnung',
}

const Section = React.memo(
	({ title, children }: { title: string; children: React.ReactNode }): JSX.Element => (
		<Card>
			<Txt variant="subtitle1" fontWeight="bold" sx={sxSectionTitle}>
				{title}
			</Txt>
			{children}
		</Card>
	)
)

// Die ganze Karte je Kategorie: jedes Gericht mit Status, bei fehlender Kennzeichnung mit den Labels
// und der Zutat als Grund.
const ItemsByCategory = React.memo(
	({ categories }: { categories: MenuImportReport['itemsByCategory'] }): JSX.Element => (
		<Section
			title={`Gerichte auf der Karte: ${
				categories
					.flatMap((category) => category.items)
					.filter((item) => item.status === 'IMPORTABLE').length
			} von ${categories.flatMap((category) => category.items).length} importierbar`}
		>
			{categories.map((category) => (
				<Box key={category.title} sx={sxCategory}>
					<Txt variant="subtitle2" fontWeight="bold">
						{`${category.title} (${
							category.items.filter((item) => item.status === 'IMPORTABLE').length
						} von ${category.items.length})`}
					</Txt>
					<Table size="small">
						<TableBody>
							{category.items.map((item) => (
								<TableRow key={`${item.type}-${item.name}`}>
									<TableCell sx={sxItemNameCell}>{item.name}</TableCell>
									<TableCell sx={sxStatusCell}>
										{item.status === 'IMPORTABLE' ? (
											<CheckCircleOutline color="success" fontSize="small" />
										) : (
											<Box sx={sxStatus}>
												<CancelOutlined color="error" fontSize="small" />
												<Txt variant="body2">{DROP_REASONS[item.status]}</Txt>
											</Box>
										)}
									</TableCell>
									<TableCell>
										<Box sx={sxGaps}>
											{item.gaps.map((gap) => (
												<Box key={gap.label} sx={sxGap}>
													{/* Kurzform: „Glutenhaltiges Getreide – Weizen sowie …" → Weizen, „Eier und daraus …" → Eier */}
													<Chip
														size="small"
														label={
															gap.label.match(/– (.+?) sowie/)?.[1] ??
															gap.label.split(' und daraus')[0]
														}
													/>
													<Txt variant="body2" color="text.secondary">
														{`(${gap.reasons.join(', ')})`}
													</Txt>
												</Box>
											))}
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			))}
		</Section>
	)
)

type Props = {
	report: MenuImportReport
	loading: boolean
	onCancel?: () => void
	// Ohne onConfirm ist der Bericht nur zum Ansehen, etwa für einen schon importierten Kartenstand.
	onConfirm?: () => void
	confirmLabel?: string
}

// Vor dem Import sieht der Root-User hier, was der Import anrichten würde, und bestätigt ihn.
const MenuImportDetails = ({
	report,
	loading,
	onCancel,
	onConfirm,
	confirmLabel,
}: Props): JSX.Element => {
	const {
		sourceCategoryCount,
		sourceProductCount,
		sourceOptionGroupCount,
		sourceOptionCount,
		itemsByCategory,
		counts,
		source: importMenuSource,
	} = report
	const countRows = React.useMemo(
		() => [
			{ label: 'Kategorien', imported: counts.categories, total: sourceCategoryCount },
			{ label: 'Produkte', imported: counts.products, total: sourceProductCount },
			{ label: 'Options-Gruppen', imported: counts.optionGroups, total: sourceOptionGroupCount },
			{ label: 'Optionen', imported: counts.options, total: sourceOptionCount },
		],
		[counts, sourceCategoryCount, sourceProductCount, sourceOptionGroupCount, sourceOptionCount]
	)

	// Nur der Bericht ohne Buttons kommt in ein eigenes Fenster, samt der Styles der App (MUI legt
	// sie teils nur im CSSOM ab), und von dort in den Druckdialog, wo er sich als PDF sichern lässt.
	const reportRef = React.useRef<HTMLDivElement>(null)
	const handlePrint = React.useCallback(() => {
		const css = Array.from(document.styleSheets)
			.flatMap((sheet) => {
				try {
					return Array.from(sheet.cssRules).map((rule) => rule.cssText)
				} catch {
					return []
				}
			})
			.join('\n')
		const fontLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
			.map((link) => link.outerHTML)
			.join('')
		const printWindow = window.open('', '_blank')!
		printWindow.document.write(
			`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8" /><title>Menü-Import Bericht – ${
				importMenuSource ?? ''
			}</title>${fontLinks}<style>${css}</style><style>body { margin: 1.5rem; }</style></head><body>${
				reportRef.current!.outerHTML
			}</body></html>`
		)
		printWindow.document.close()
		printWindow.onload = () => printWindow.print()
	}, [importMenuSource])

	return (
		<Box sx={sxPage}>
			<Box ref={reportRef} sx={sxPage}>
				<Card>
					<Txt variant="h6" fontWeight="bold">
						Menü-Import Bericht
					</Txt>
					<Txt variant="body2" sx={sxSource}>
						{'Quelle: '}
						{importMenuSource?.startsWith('http') ? (
							<Link href={importMenuSource} target="_blank" rel="noreferrer">
								{importMenuSource}
							</Link>
						) : (
							importMenuSource ?? 'nicht angegeben'
						)}
					</Txt>
				</Card>

				<Section title="Import">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={sxHeadCell}>Wird importiert</TableCell>
								<TableCell sx={sxNumberHeadCell}>Import</TableCell>
								<TableCell sx={sxNumberHeadCell}>Quelle</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{countRows.map((row) => (
								<TableRow key={row.label}>
									<TableCell>{row.label}</TableCell>
									<TableCell sx={sxNumberCell}>{row.imported}</TableCell>
									<TableCell sx={sxNumberCell}>{row.total}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Section>

				<ItemsByCategory categories={itemsByCategory} />
			</Box>

			<Box sx={sxFooterButtons}>
				<Button variant="outlined" onClick={handlePrint}>
					PDF
				</Button>
				{onCancel ? (
					<Button variant="outlined" onClick={onCancel} disabled={loading}>
						{onConfirm ? 'Abbrechen' : 'Schließen'}
					</Button>
				) : null}
				{onConfirm ? (
					<Button variant="contained" onClick={onConfirm} loading={loading}>
						{confirmLabel}
					</Button>
				) : null}
			</Box>
		</Box>
	)
}

export default React.memo(MenuImportDetails)
